import { ShieldAlert, Flame, HeartPulse, AlertTriangle, MapPin, CheckCircle } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";

const typeIcons = { security: ShieldAlert, fire: Flame, medical: HeartPulse, accident: AlertTriangle };
const typeColors = {
  security: { dot: "bg-red-500", badge: "bg-red-100 text-red-800 hover:bg-red-100", line: "border-red-200" },
  fire: { dot: "bg-orange-500", badge: "bg-orange-100 text-orange-800 hover:bg-orange-100", line: "border-orange-200" },
  medical: { dot: "bg-blue-500", badge: "bg-blue-100 text-blue-800 hover:bg-blue-100", line: "border-blue-200" },
  accident: { dot: "bg-yellow-500", badge: "bg-yellow-100 text-yellow-800 hover:bg-yellow-100", line: "border-yellow-200" },
};

function formatDateTime(isoStr) {
  const d = new Date(isoStr);
  return {
    date: d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }),
    time: d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }),
  };
}

function groupByDate(alerts) {
  const groups = {};
  for (const alert of alerts) {
    const { date } = formatDateTime(alert.created_at);
    if (!groups[date]) groups[date] = [];
    groups[date].push(alert);
  }
  return Object.entries(groups);
}

export default function AlertTimeline({ alerts = [], onResolve }) {
  const navigate = useNavigate();
  const grouped = groupByDate(alerts);

  if (alerts.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400">
        <p className="text-sm font-body">No alerts to display on timeline</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="alert-timeline">
      {grouped.map(([date, dayAlerts]) => (
        <div key={date}>
          <div className="sticky top-0 z-10 bg-[#F3F4F6] py-1 mb-3">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 font-body">{date}</p>
          </div>
          <div className="relative pl-6">
            {/* Vertical line */}
            <div className="absolute left-[9px] top-2 bottom-2 w-px bg-gray-200" />

            <div className="space-y-3">
              {dayAlerts.map((alert) => {
                const Icon = typeIcons[alert.alert_type] || ShieldAlert;
                const colors = typeColors[alert.alert_type] || typeColors.security;
                const { time } = formatDateTime(alert.created_at);
                const isResolved = alert.status === "resolved";

                return (
                  <div key={alert.alert_id} className="relative flex gap-3" data-testid={`timeline-item-${alert.alert_id}`}>
                    {/* Dot on line */}
                    <div className={`absolute -left-6 top-1.5 w-[18px] h-[18px] rounded-full border-2 border-white ${colors.dot} flex items-center justify-center shadow-sm`}>
                      <Icon className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </div>

                    {/* Card */}
                    <div className={`flex-1 bg-white border ${isResolved ? "border-gray-200 opacity-70" : "border-gray-200"} rounded-sm p-3 hover:shadow-sm transition-shadow duration-100`}>
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2">
                          <Badge className={`text-[10px] px-1.5 py-0 rounded-sm ${colors.badge}`}>
                            {alert.alert_type}
                          </Badge>
                          {isResolved && (
                            <Badge className="text-[10px] px-1.5 py-0 rounded-sm bg-green-100 text-green-800 hover:bg-green-100">
                              resolved
                            </Badge>
                          )}
                        </div>
                        <span className="text-[10px] text-gray-400 font-body whitespace-nowrap">{time}</span>
                      </div>

                      <p className="text-sm text-gray-800 font-body mb-1">{alert.description}</p>

                      <div className="flex items-center justify-between mt-2">
                        <p className="text-[10px] text-gray-400 font-body">{alert.user_name}</p>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => navigate(`/dashboard?lat=${alert.latitude}&lng=${alert.longitude}`)}
                            className="h-5 text-[10px] text-blue-700 hover:text-blue-800 hover:bg-blue-50 rounded-sm px-1.5"
                            data-testid={`timeline-map-${alert.alert_id}`}
                          >
                            <MapPin className="w-3 h-3 mr-0.5" /> Map
                          </Button>
                          {!isResolved && onResolve && (
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => onResolve(alert.alert_id)}
                              className="h-5 text-[10px] text-green-700 hover:text-green-800 hover:bg-green-50 rounded-sm px-1.5"
                              data-testid={`timeline-resolve-${alert.alert_id}`}
                            >
                              <CheckCircle className="w-3 h-3 mr-0.5" /> Resolve
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
